import { useState, useEffect } from 'react';
import type { EditableContent } from '../../hooks/useEditableContent';

interface EditableContentEditorProps {
  content: EditableContent;
  onSave: (content: EditableContent) => boolean;
  onCancel: () => void;
}

export default function EditableContentEditor({ content, onSave, onCancel }: EditableContentEditorProps) {
  const [editedContent, setEditedContent] = useState<EditableContent>(content);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setEditedContent(content);
  }, [content]);

  const handleSave = () => {
    setIsSaving(true);
    const success = onSave(editedContent);
    setSaveStatus(success ? 'success' : 'error');
    setIsSaving(false);
    
    if (success) {
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    }
  };

  const handleCancel = () => {
    setEditedContent(content);
    onCancel();
  };

  const updateField = (field: keyof EditableContent, value: any) => {
    setEditedContent(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (parent: keyof EditableContent, index: number, field: string, value: any) => {
    setEditedContent(prev => {
      const parentArray = [...(prev[parent] as any[])];
      parentArray[index] = { ...parentArray[index], [field]: value };
      return { ...prev, [parent]: parentArray };
    });
  };

  const addMethod = () => {
    setEditedContent(prev => ({
      ...prev,
      methods: [...prev.methods, { icon: 'ri-tools-line', title: 'Nouvelle méthode', description: 'Description de la méthode' }]
    }));
  };

  const removeMethod = (index: number) => {
    setEditedContent(prev => ({
      ...prev,
      methods: prev.methods.filter((_, i) => i !== index)
    }));
  };

  const addTarif = () => {
    setEditedContent(prev => ({
      ...prev,
      tarifs: [...prev.tarifs, {
        title: 'Nouveau tarif',
        subtitle: 'Description',
        price: 'Sur devis',
        priceSubtitle: 'Selon besoins',
        features: ['Caractéristique 1', 'Caractéristique 2'],
        buttonText: 'Demander un devis'
      }]
    }));
  };

  const removeTarif = (index: number) => {
    setEditedContent(prev => ({
      ...prev,
      tarifs: prev.tarifs.filter((_, i) => i !== index)
    }));
  };

  const addTarifFeature = (tarifIndex: number) => {
    setEditedContent(prev => {
      const newTarifs = [...prev.tarifs];
      newTarifs[tarifIndex] = {
        ...newTarifs[tarifIndex],
        features: [...newTarifs[tarifIndex].features, 'Nouvelle caractéristique']
      };
      return { ...prev, tarifs: newTarifs };
    });
  };

  const removeTarifFeature = (tarifIndex: number, featureIndex: number) => {
    setEditedContent(prev => {
      const newTarifs = [...prev.tarifs];
      newTarifs[tarifIndex] = {
        ...newTarifs[tarifIndex],
        features: newTarifs[tarifIndex].features.filter((_, i) => i !== featureIndex)
      };
      return { ...prev, tarifs: newTarifs };
    });
  };

  const updateTarifFeature = (tarifIndex: number, featureIndex: number, value: string) => {
    setEditedContent(prev => {
      const newTarifs = [...prev.tarifs];
      const newFeatures = [...newTarifs[tarifIndex].features];
      newFeatures[featureIndex] = value;
      newTarifs[tarifIndex] = { ...newTarifs[tarifIndex], features: newFeatures };
      return { ...prev, tarifs: newTarifs };
    });
  };

  const addFAQ = () => {
    setEditedContent(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: 'Nouvelle question ?', answer: 'Réponse à la question.' }]
    }));
  };

  const removeFAQ = (index: number) => {
    setEditedContent(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-2xl z-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Modifier le contenu de la page</h2>
                <p className="text-sm text-gray-600 mt-1">Modifiez les textes et images de cette page</p>
              </div>
              <div className="flex items-center gap-3">
                {saveStatus === 'success' && (
                  <span className="text-sm text-green-600 flex items-center gap-2">
                    <i className="ri-check-line"></i>
                    Sauvegardé
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span className="text-sm text-red-600 flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    Erreur
                  </span>
                )}
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-6 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 space-y-8">
            {/* Hero Section */}
            <section className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Section Hero</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre principal</label>
                  <input
                    type="text"
                    value={editedContent.pageTitle}
                    onChange={(e) => updateField('pageTitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sous-titre</label>
                  <textarea
                    value={editedContent.pageSubtitle}
                    onChange={(e) => updateField('pageSubtitle', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">URL de l'image hero</label>
                  <input
                    type="text"
                    value={editedContent.heroImage.url}
                    onChange={(e) => updateField('heroImage', { ...editedContent.heroImage, url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
              </div>
            </section>

            {/* Description Section */}
            <section className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Section Description</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                  <input
                    type="text"
                    value={editedContent.descriptionTitle}
                    onChange={(e) => updateField('descriptionTitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Paragraphe 1</label>
                  <textarea
                    value={editedContent.descriptionText1}
                    onChange={(e) => updateField('descriptionText1', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Paragraphe 2</label>
                  <textarea
                    value={editedContent.descriptionText2}
                    onChange={(e) => updateField('descriptionText2', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">URL de l'image</label>
                  <input
                    type="text"
                    value={editedContent.descriptionImage.url}
                    onChange={(e) => updateField('descriptionImage', { ...editedContent.descriptionImage, url: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
              </div>
            </section>

            {/* Methods Section */}
            <section className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Section Méthodes</h3>
                <button
                  onClick={addMethod}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm whitespace-nowrap"
                >
                  + Ajouter une méthode
                </button>
              </div>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre de la section</label>
                  <input
                    type="text"
                    value={editedContent.methodsTitle}
                    onChange={(e) => updateField('methodsTitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sous-titre</label>
                  <input
                    type="text"
                    value={editedContent.methodsSubtitle}
                    onChange={(e) => updateField('methodsSubtitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              <div className="space-y-4">
                {editedContent.methods.map((method, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Méthode {index + 1}</span>
                      <button
                        onClick={() => removeMethod(index)}
                        className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                      >
                        Supprimer
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Icône (classe Remix Icon)</label>
                        <input
                          type="text"
                          value={method.icon}
                          onChange={(e) => updateNestedField('methods', index, 'icon', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="ri-tools-line"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Titre</label>
                        <input
                          type="text"
                          value={method.title}
                          onChange={(e) => updateNestedField('methods', index, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Description</label>
                        <textarea
                          value={method.description}
                          onChange={(e) => updateNestedField('methods', index, 'description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Tarifs Section */}
            <section className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Section Tarifs</h3>
                <button
                  onClick={addTarif}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm whitespace-nowrap"
                >
                  + Ajouter un tarif
                </button>
              </div>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre de la section</label>
                  <input
                    type="text"
                    value={editedContent.tarifsTitle}
                    onChange={(e) => updateField('tarifsTitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sous-titre</label>
                  <input
                    type="text"
                    value={editedContent.tarifsSubtitle}
                    onChange={(e) => updateField('tarifsSubtitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              <div className="space-y-4">
                {editedContent.tarifs.map((tarif, tarifIndex) => (
                  <div key={tarifIndex} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Tarif {tarifIndex + 1}</span>
                      <button
                        onClick={() => removeTarif(tarifIndex)}
                        className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                      >
                        Supprimer
                      </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Titre</label>
                        <input
                          type="text"
                          value={tarif.title}
                          onChange={(e) => updateNestedField('tarifs', tarifIndex, 'title', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Sous-titre</label>
                        <input
                          type="text"
                          value={tarif.subtitle}
                          onChange={(e) => updateNestedField('tarifs', tarifIndex, 'subtitle', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Prix</label>
                        <input
                          type="text"
                          value={tarif.price}
                          onChange={(e) => updateNestedField('tarifs', tarifIndex, 'price', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Sous-titre du prix</label>
                        <input
                          type="text"
                          value={tarif.priceSubtitle}
                          onChange={(e) => updateNestedField('tarifs', tarifIndex, 'priceSubtitle', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs text-gray-600">Caractéristiques</label>
                        <button
                          onClick={() => addTarifFeature(tarifIndex)}
                          className="text-xs text-brand-600 hover:text-brand-700 whitespace-nowrap"
                        >
                          + Ajouter
                        </button>
                      </div>
                      <div className="space-y-2">
                        {tarif.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={feature}
                              onChange={(e) => updateTarifFeature(tarifIndex, featureIndex, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <button
                              onClick={() => removeTarifFeature(tarifIndex, featureIndex)}
                              className="text-red-600 hover:text-red-700 whitespace-nowrap"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Texte du bouton</label>
                      <input
                        type="text"
                        value={tarif.buttonText}
                        onChange={(e) => updateNestedField('tarifs', tarifIndex, 'buttonText', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ Section */}
            <section className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">Section FAQ</h3>
                <button
                  onClick={addFAQ}
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors text-sm whitespace-nowrap"
                >
                  + Ajouter une question
                </button>
              </div>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre de la section</label>
                  <input
                    type="text"
                    value={editedContent.faqTitle}
                    onChange={(e) => updateField('faqTitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sous-titre</label>
                  <input
                    type="text"
                    value={editedContent.faqSubtitle}
                    onChange={(e) => updateField('faqSubtitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
              </div>
              <div className="space-y-4">
                {editedContent.faqs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Question {index + 1}</span>
                      <button
                        onClick={() => removeFAQ(index)}
                        className="text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                      >
                        Supprimer
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Question</label>
                        <input
                          type="text"
                          value={faq.question}
                          onChange={(e) => updateNestedField('faqs', index, 'question', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Réponse</label>
                        <textarea
                          value={faq.answer}
                          onChange={(e) => updateNestedField('faqs', index, 'answer', e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Form Section */}
            <section className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Section Formulaire</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre</label>
                  <input
                    type="text"
                    value={editedContent.formTitle}
                    onChange={(e) => updateField('formTitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sous-titre</label>
                  <input
                    type="text"
                    value={editedContent.formSubtitle}
                    onChange={(e) => updateField('formSubtitle', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent text-sm"
                  />
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}